const spectrogramAnalysisCache = new Map();
const pendingSpectrogramAnalysisLoads = new Map();

export const SPECTROGRAM_MIN_ZOOM = 1;
export const SPECTROGRAM_MAX_ZOOM = 8;
export const SPECTROGRAM_ZOOM_STEP = 0.25;
export const SPECTROGRAM_WIDTH = 640;
export const SPECTROGRAM_HEIGHT = 280;
export const SPECTROGRAM_DEFAULT_MAX_HZ = 20000;
const SPECTROGRAM_FFT_SIZE = 1024;
const SPECTROGRAM_FREQUENCY_TICK_COUNT = 4;
const SPECTROGRAM_LOG_SCALE_MIN_HZ = 20;
export const SPECTROGRAM_TIME_TICK_COUNT = 7;
export const WAVEFORM_HEIGHT = 112;
const WAVEFORM_DISPLAY_RANGE_MV = 0.5;
export const WAVEFORM_AXIS_TICKS_MV = [
  WAVEFORM_DISPLAY_RANGE_MV,
  0,
  -WAVEFORM_DISPLAY_RANGE_MV,
];
export const AUDIO_ANALYSIS_AXIS_GRID_CLASS =
  "grid grid-cols-[92px_minmax(0,1fr)] gap-2";

export function formatSpectrogramZoomLabel(zoomLevel) {
  const safeZoomLevel = Number.isFinite(zoomLevel)
    ? zoomLevel
    : SPECTROGRAM_MIN_ZOOM;

  return `${safeZoomLevel
    .toFixed(2)
    .replace(/\.00$/, "")
    .replace(/(\.\d)0$/, "$1")}x`;
}

export function getCanvasContext(canvas) {
  if (!canvas?.getContext) {
    return null;
  }

  try {
    return canvas.getContext("2d");
  } catch (_error) {
    return null;
  }
}

export function clampNumber(value, minValue, maxValue) {
  return Math.min(Math.max(value, minValue), maxValue);
}

export function formatMediaTimeLabel(value, options = {}) {
  if (!Number.isFinite(value) || value < 0) {
    return options.useSeconds ? "00.0" : "0:00";
  }

  if (options.useSeconds) {
    const totalTenths = Math.round(value * 10);
    const wholeSeconds = Math.floor(totalTenths / 10);
    const tenths = totalTenths % 10;

    return `${String(wholeSeconds).padStart(2, "0")}.${tenths}`;
  }

  const totalSeconds = Math.floor(value);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

export function buildTimeAxisTicks(startTime, endTime, tickCount) {
  const safeTickCount = Math.max(Math.floor(tickCount) || 0, 2);
  const safeStartTime = Number.isFinite(startTime) ? startTime : 0;
  const safeEndTime = Number.isFinite(endTime) ? endTime : safeStartTime;
  const duration = Math.max(safeEndTime - safeStartTime, 0);
  const useSeconds = duration < 60;

  return Array.from({ length: safeTickCount }, (_, index) => {
    const progress = index / Math.max(safeTickCount - 1, 1);
    const time = safeStartTime + duration * progress;

    return {
      time,
      label: formatMediaTimeLabel(time, { useSeconds }),
    };
  });
}

function resolveSpectrogramMaxFrequency() {
  return SPECTROGRAM_DEFAULT_MAX_HZ;
}

function getLogScaleMinimumFrequency(maxFrequencyHz) {
  return Math.min(
    SPECTROGRAM_LOG_SCALE_MIN_HZ,
    Math.max(maxFrequencyHz, 1),
  );
}

export function normalizeFrequencyPosition(
  frequencyHz,
  maxFrequencyHz,
  options = {},
) {
  const safeMaxFrequencyHz = Math.max(maxFrequencyHz, 1);
  const clampedFrequencyHz = clampNumber(frequencyHz, 0, safeMaxFrequencyHz);

  if (!options.logScale) {
    return clampedFrequencyHz / safeMaxFrequencyHz;
  }

  const minLogFrequencyHz = getLogScaleMinimumFrequency(safeMaxFrequencyHz);
  if (safeMaxFrequencyHz <= minLogFrequencyHz) {
    return clampedFrequencyHz / safeMaxFrequencyHz;
  }

  if (clampedFrequencyHz <= 0) {
    return 0;
  }

  const safeClampedFrequencyHz = Math.max(clampedFrequencyHz, minLogFrequencyHz);
  const logStart = Math.log10(minLogFrequencyHz);
  const logEnd = Math.log10(safeMaxFrequencyHz);

  return clampNumber(
    (Math.log10(safeClampedFrequencyHz) - logStart) / (logEnd - logStart),
    0,
    1,
  );
}

function denormalizeFrequencyPosition(
  normalizedValue,
  maxFrequencyHz,
  options = {},
) {
  const safeMaxFrequencyHz = Math.max(maxFrequencyHz, 1);
  const clampedNormalizedValue = clampNumber(normalizedValue, 0, 1);

  if (!options.logScale) {
    return clampedNormalizedValue * safeMaxFrequencyHz;
  }

  const minLogFrequencyHz = getLogScaleMinimumFrequency(safeMaxFrequencyHz);
  if (safeMaxFrequencyHz <= minLogFrequencyHz) {
    return clampedNormalizedValue * safeMaxFrequencyHz;
  }

  if (clampedNormalizedValue <= 0) {
    return 0;
  }

  const logStart = Math.log10(minLogFrequencyHz);
  const logEnd = Math.log10(safeMaxFrequencyHz);
  return 10 ** (logStart + clampedNormalizedValue * (logEnd - logStart));
}

function buildFrequencyTickCandidates(maxFrequencyHz) {
  const safeMaxFrequencyHz = Math.max(maxFrequencyHz, 1);
  const highestExponent = Math.ceil(Math.log10(safeMaxFrequencyHz));
  const candidates = new Set([
    safeMaxFrequencyHz,
    getLogScaleMinimumFrequency(safeMaxFrequencyHz),
  ]);

  for (let exponent = 0; exponent <= highestExponent; exponent += 1) {
    [1, 2, 5].forEach((multiplier) => {
      const nextValue = multiplier * 10 ** exponent;

      if (nextValue > 0 && nextValue < safeMaxFrequencyHz) {
        candidates.add(nextValue);
      }
    });
  }

  return [...candidates].sort((left, right) => left - right);
}

function findClosestFrequencyTick(targetFrequencyHz, candidates) {
  if (!Array.isArray(candidates) || !candidates.length) {
    return targetFrequencyHz;
  }

  return candidates.reduce((closestFrequencyHz, candidateFrequencyHz) => {
    return Math.abs(candidateFrequencyHz - targetFrequencyHz) <
      Math.abs(closestFrequencyHz - targetFrequencyHz)
      ? candidateFrequencyHz
      : closestFrequencyHz;
  }, candidates[0]);
}

export function buildFrequencyAxisTicks(maxFrequencyHz, options = {}) {
  const safeMaxFrequencyHz = Math.max(maxFrequencyHz, 1000);

  if (!options.logScale) {
    return Array.from(
      { length: SPECTROGRAM_FREQUENCY_TICK_COUNT + 1 },
      (_, index) =>
        Math.round(
          safeMaxFrequencyHz *
            ((SPECTROGRAM_FREQUENCY_TICK_COUNT - index) /
              SPECTROGRAM_FREQUENCY_TICK_COUNT),
        ),
    ).sort((left, right) => right - left);
  }

  const tickCandidates = buildFrequencyTickCandidates(safeMaxFrequencyHz);
  const positiveTicks = Array.from(
    { length: SPECTROGRAM_FREQUENCY_TICK_COUNT },
    (_, index) => {
      const progress =
        SPECTROGRAM_FREQUENCY_TICK_COUNT <= 1
          ? 1
          : 1 -
            index / Math.max(SPECTROGRAM_FREQUENCY_TICK_COUNT - 1, 1);
      const targetFrequencyHz =
        progress <= 0
          ? getLogScaleMinimumFrequency(safeMaxFrequencyHz)
          : denormalizeFrequencyPosition(progress, safeMaxFrequencyHz, {
              logScale: true,
            });

      return findClosestFrequencyTick(targetFrequencyHz, tickCandidates);
    },
  );

  return [...new Set([...positiveTicks, 0])].sort((left, right) => right - left);
}

export function formatFrequencyAxisLabel(frequencyHz) {
  if (frequencyHz >= 1000) {
    const valueKhz = frequencyHz / 1000;
    return Number.isInteger(valueKhz)
      ? `${valueKhz} kHz`
      : `${valueKhz.toFixed(1)} kHz`;
  }

  return `${Math.round(frequencyHz)} Hz`;
}

export function formatWaveformAmplitudeLabel(
  amplitudeMv,
  millivoltsLabel,
) {
  const roundedAmplitude = Math.abs(amplitudeMv) < 1e-6 ? 0 : amplitudeMv;
  return `${roundedAmplitude.toFixed(roundedAmplitude === 0 ? 0 : 1)} ${millivoltsLabel}`;
}

export function normalizeWaveformAmplitude(amplitudeMv) {
  return (
    (WAVEFORM_DISPLAY_RANGE_MV -
      clampNumber(
        amplitudeMv,
        -WAVEFORM_DISPLAY_RANGE_MV,
        WAVEFORM_DISPLAY_RANGE_MV,
      )) /
    (WAVEFORM_DISPLAY_RANGE_MV * 2)
  );
}

function buildSpectrogramWindowValues(windowSize) {
  const values = new Float32Array(windowSize);

  for (let index = 0; index < windowSize; index += 1) {
    values[index] =
      0.5 - 0.5 * Math.cos((2 * Math.PI * index) / (windowSize - 1));
  }

  return values;
}

function mixAudioBufferToMono(audioBuffer) {
  if (
    !audioBuffer ||
    audioBuffer.length <= 0 ||
    audioBuffer.numberOfChannels <= 0
  ) {
    return new Float32Array(0);
  }

  if (audioBuffer.numberOfChannels === 1) {
    return audioBuffer.getChannelData(0).slice();
  }

  const monoChannel = new Float32Array(audioBuffer.length);

  for (
    let channelIndex = 0;
    channelIndex < audioBuffer.numberOfChannels;
    channelIndex += 1
  ) {
    const channelData = audioBuffer.getChannelData(channelIndex);
    for (
      let sampleIndex = 0;
      sampleIndex < audioBuffer.length;
      sampleIndex += 1
    ) {
      monoChannel[sampleIndex] += channelData[sampleIndex];
    }
  }

  for (
    let sampleIndex = 0;
    sampleIndex < audioBuffer.length;
    sampleIndex += 1
  ) {
    monoChannel[sampleIndex] /= audioBuffer.numberOfChannels;
  }

  return monoChannel;
}

function buildWaveformColumns(monoChannel) {
  if (!monoChannel?.length) {
    return [];
  }

  return Array.from({ length: SPECTROGRAM_WIDTH }, (_, columnIndex) => {
    const startSampleIndex = Math.min(
      Math.floor((columnIndex / SPECTROGRAM_WIDTH) * monoChannel.length),
      monoChannel.length - 1,
    );
    const endSampleIndex = Math.max(
      startSampleIndex + 1,
      Math.min(
        Math.ceil(((columnIndex + 1) / SPECTROGRAM_WIDTH) * monoChannel.length),
        monoChannel.length,
      ),
    );
    let minSample = Number.POSITIVE_INFINITY;
    let maxSample = Number.NEGATIVE_INFINITY;

    for (
      let sampleIndex = startSampleIndex;
      sampleIndex < endSampleIndex;
      sampleIndex += 1
    ) {
      const sampleValue = monoChannel[sampleIndex] || 0;
      minSample = Math.min(minSample, sampleValue);
      maxSample = Math.max(maxSample, sampleValue);
    }

    if (!Number.isFinite(minSample) || !Number.isFinite(maxSample)) {
      minSample = 0;
      maxSample = 0;
    }

    return {
      minAmplitudeMv: clampNumber(
        minSample * WAVEFORM_DISPLAY_RANGE_MV,
        -WAVEFORM_DISPLAY_RANGE_MV,
        WAVEFORM_DISPLAY_RANGE_MV,
      ),
      maxAmplitudeMv: clampNumber(
        maxSample * WAVEFORM_DISPLAY_RANGE_MV,
        -WAVEFORM_DISPLAY_RANGE_MV,
        WAVEFORM_DISPLAY_RANGE_MV,
      ),
    };
  });
}

function buildSpectrogramBandRanges(
  height,
  maxFrequencyHz,
  sampleRate,
  fftSize,
) {
  const bandRanges = [];
  const nyquistHz = sampleRate / 2;
  const highestBinIndex = Math.floor(fftSize / 2);
  const hzPerBin = sampleRate / fftSize;

  for (let row = 0; row < height; row += 1) {
    const upperFrequencyHz = denormalizeFrequencyPosition(
      1 - row / Math.max(height, 1),
      maxFrequencyHz,
    );
    const lowerFrequencyHz = denormalizeFrequencyPosition(
      1 - (row + 1) / Math.max(height, 1),
      maxFrequencyHz,
    );
    const emphasis = 1 + (row / Math.max(height - 1, 1)) * 0.35;

    if (lowerFrequencyHz >= nyquistHz) {
      bandRanges.push({
        startBinIndex: 0,
        endBinIndex: -1,
        emphasis,
        hasData: false,
      });
      continue;
    }

    const clampedUpperFrequencyHz = clampNumber(upperFrequencyHz, 0, nyquistHz);
    const clampedLowerFrequencyHz = clampNumber(lowerFrequencyHz, 0, nyquistHz);
    const startBinIndex = clampNumber(
      Math.floor(clampedLowerFrequencyHz / hzPerBin),
      0,
      highestBinIndex,
    );
    const endBinIndex = clampNumber(
      Math.ceil(clampedUpperFrequencyHz / hzPerBin),
      startBinIndex,
      highestBinIndex,
    );

    bandRanges.push({
      startBinIndex,
      endBinIndex,
      emphasis,
      hasData: true,
    });
  }

  return bandRanges;
}

function buildSpectrogramFrame(
  monoChannel,
  centerSampleIndex,
  fftSize,
  windowValues,
) {
  const frame = new Float32Array(fftSize);
  const firstSampleIndex = centerSampleIndex - Math.floor(fftSize / 2);

  for (let sampleOffset = 0; sampleOffset < fftSize; sampleOffset += 1) {
    const sampleIndex = firstSampleIndex + sampleOffset;
    const sampleValue =
      sampleIndex >= 0 && sampleIndex < monoChannel.length
        ? monoChannel[sampleIndex]
        : 0;
    frame[sampleOffset] = sampleValue * windowValues[sampleOffset];
  }

  return frame;
}

function performFftReal(frame) {
  const frameSize = frame.length;
  const real = new Float32Array(frameSize);
  const imaginary = new Float32Array(frameSize);
  real.set(frame);

  let reversedIndex = 0;
  for (let currentIndex = 1; currentIndex < frameSize; currentIndex += 1) {
    let bitMask = frameSize >> 1;
    while (reversedIndex & bitMask) {
      reversedIndex ^= bitMask;
      bitMask >>= 1;
    }
    reversedIndex ^= bitMask;

    if (currentIndex < reversedIndex) {
      const realValue = real[currentIndex];
      real[currentIndex] = real[reversedIndex];
      real[reversedIndex] = realValue;

      const imaginaryValue = imaginary[currentIndex];
      imaginary[currentIndex] = imaginary[reversedIndex];
      imaginary[reversedIndex] = imaginaryValue;
    }
  }

  for (let blockSize = 2; blockSize <= frameSize; blockSize *= 2) {
    const halfBlockSize = blockSize / 2;
    const phaseStep = (-2 * Math.PI) / blockSize;

    for (
      let blockStartIndex = 0;
      blockStartIndex < frameSize;
      blockStartIndex += blockSize
    ) {
      for (let blockOffset = 0; blockOffset < halfBlockSize; blockOffset += 1) {
        const angle = blockOffset * phaseStep;
        const cosine = Math.cos(angle);
        const sine = Math.sin(angle);
        const evenIndex = blockStartIndex + blockOffset;
        const oddIndex = evenIndex + halfBlockSize;
        const temporaryRealValue =
          cosine * real[oddIndex] - sine * imaginary[oddIndex];
        const temporaryImaginaryValue =
          sine * real[oddIndex] + cosine * imaginary[oddIndex];

        real[oddIndex] = real[evenIndex] - temporaryRealValue;
        imaginary[oddIndex] = imaginary[evenIndex] - temporaryImaginaryValue;
        real[evenIndex] += temporaryRealValue;
        imaginary[evenIndex] += temporaryImaginaryValue;
      }
    }
  }

  const magnitudes = new Float32Array(frameSize / 2 + 1);

  for (let index = 0; index < magnitudes.length; index += 1) {
    magnitudes[index] = Math.hypot(real[index], imaginary[index]);
  }

  return magnitudes;
}

function normalizeSpectrogramMatrix(matrix, minValue, maxValue) {
  const safeRange = Math.max(maxValue - minValue, 1e-6);

  return matrix.map((column) => {
    const normalizedColumn = new Float32Array(column.length);
    for (let row = 0; row < column.length; row += 1) {
      const normalizedValue = clampNumber(
        (column[row] - minValue) / safeRange,
        0,
        1,
      );
      normalizedColumn[row] = clampNumber(
        0.08 + Math.pow(normalizedValue, 0.68) * 0.92,
        0,
        1,
      );
    }

    return normalizedColumn;
  });
}

function smoothSpectrogramMatrix(matrix) {
  if (!matrix.length || !matrix[0]?.length) {
    return matrix;
  }

  const width = matrix.length;
  const height = matrix[0].length;
  const smoothedMatrix = Array.from(
    { length: width },
    () => new Float32Array(height),
  );

  for (let columnIndex = 0; columnIndex < width; columnIndex += 1) {
    for (let rowIndex = 0; rowIndex < height; rowIndex += 1) {
      let weightedSum = 0;
      let totalWeight = 0;

      for (let columnOffset = -1; columnOffset <= 1; columnOffset += 1) {
        const neighbourColumnIndex = columnIndex + columnOffset;
        if (neighbourColumnIndex < 0 || neighbourColumnIndex >= width) {
          continue;
        }

        for (let rowOffset = -1; rowOffset <= 1; rowOffset += 1) {
          const neighbourRowIndex = rowIndex + rowOffset;
          if (neighbourRowIndex < 0 || neighbourRowIndex >= height) {
            continue;
          }

          const weight =
            columnOffset === 0 && rowOffset === 0
              ? 4
              : columnOffset === 0 || rowOffset === 0
                ? 2
                : 1;
          weightedSum +=
            matrix[neighbourColumnIndex][neighbourRowIndex] * weight;
          totalWeight += weight;
        }
      }

      smoothedMatrix[columnIndex][rowIndex] =
        totalWeight > 0
          ? weightedSum / totalWeight
          : matrix[columnIndex][rowIndex];
    }
  }

  return smoothedMatrix;
}

function getSpectrogramPaletteColor(normalizedValue) {
  const clampedValue = clampNumber(normalizedValue, 0, 1);
  const stops = [
    { offset: 0, color: [248, 243, 236] },
    { offset: 0.18, color: [228, 218, 225] },
    { offset: 0.4, color: [160, 126, 221] },
    { offset: 0.62, color: [120, 68, 203] },
    { offset: 0.8, color: [222, 92, 121] },
    { offset: 0.92, color: [242, 131, 35] },
    { offset: 1, color: [246, 196, 79] },
  ];

  for (let index = 1; index < stops.length; index += 1) {
    if (clampedValue <= stops[index].offset) {
      const previousStop = stops[index - 1];
      const nextStop = stops[index];
      const range = Math.max(nextStop.offset - previousStop.offset, 1e-6);
      const progress = (clampedValue - previousStop.offset) / range;

      return previousStop.color.map((channelValue, channelIndex) =>
        Math.round(
          channelValue +
            (nextStop.color[channelIndex] - channelValue) * progress,
        ),
      );
    }
  }

  return stops[stops.length - 1].color;
}

export function paintSpectrogramPlaceholder(context, width, height) {
  if (!context || width <= 0 || height <= 0) {
    return;
  }

  const backgroundGradient = context.createLinearGradient(0, 0, 0, height);
  backgroundGradient.addColorStop(0, "#faf6ef");
  backgroundGradient.addColorStop(1, "#efe4d6");
  context.fillStyle = backgroundGradient;
  context.fillRect(0, 0, width, height);
}

export function paintSpectrogramHeatmap(
  context,
  width,
  height,
  matrix,
  viewportOptions = {},
) {
  if (!context || width <= 0 || height <= 0 || !matrix?.length) {
    return;
  }

  const safeSourceWidth = Math.max(matrix.length, 1);
  const safeViewportSpanRatio = clampNumber(
    viewportOptions.sourceSpanRatio ?? 1,
    1 / safeSourceWidth,
    1,
  );
  const maxViewportStartRatio = Math.max(1 - safeViewportSpanRatio, 0);
  const safeViewportStartRatio = clampNumber(
    viewportOptions.sourceStartRatio ?? 0,
    0,
    maxViewportStartRatio,
  );
  const safeSourceHeight = Math.max(matrix[0]?.length || 0, 1);
  const sourceSpanWidth =
    safeViewportSpanRatio * Math.max(safeSourceWidth - 1, 0);
  const useLogScale = Boolean(viewportOptions.logScale);
  const safeMaxFrequencyHz = Math.max(
    viewportOptions.maxFrequencyHz ?? SPECTROGRAM_DEFAULT_MAX_HZ,
    1,
  );

  const sampleMatrixValue = (sourceColumnIndex, sourceRowPosition) => {
    const lowerSourceRowIndex = clampNumber(
      Math.floor(sourceRowPosition),
      0,
      safeSourceHeight - 1,
    );
    const upperSourceRowIndex = clampNumber(
      Math.ceil(sourceRowPosition),
      lowerSourceRowIndex,
      safeSourceHeight - 1,
    );
    const rowBlend =
      lowerSourceRowIndex === upperSourceRowIndex
        ? 0
        : sourceRowPosition - lowerSourceRowIndex;

    return (
      (matrix[sourceColumnIndex]?.[lowerSourceRowIndex] || 0) * (1 - rowBlend) +
      (matrix[sourceColumnIndex]?.[upperSourceRowIndex] || 0) * rowBlend
    );
  };

  const resolveSourceRowPosition = (rowIndex) => {
    if (!useLogScale) {
      return (rowIndex / Math.max(height - 1, 1)) * (safeSourceHeight - 1);
    }

    const normalizedDisplayFrequency = clampNumber(
      1 - rowIndex / Math.max(height - 1, 1),
      0,
      1,
    );
    const displayFrequencyHz = denormalizeFrequencyPosition(
      normalizedDisplayFrequency,
      safeMaxFrequencyHz,
      { logScale: true },
    );
    const sourceNormalizedFrequency = normalizeFrequencyPosition(
      displayFrequencyHz,
      safeMaxFrequencyHz,
    );

    return (1 - sourceNormalizedFrequency) * (safeSourceHeight - 1);
  };

  if (
    typeof context.createImageData === "function" &&
    typeof context.putImageData === "function"
  ) {
    const imageData = context.createImageData(width, height);

    for (let columnIndex = 0; columnIndex < width; columnIndex += 1) {
      const sourcePosition =
        safeViewportStartRatio * Math.max(safeSourceWidth - 1, 0) +
        (columnIndex / Math.max(width - 1, 1)) * sourceSpanWidth;
      const lowerSourceIndex = clampNumber(
        Math.floor(sourcePosition),
        0,
        safeSourceWidth - 1,
      );
      const upperSourceIndex = clampNumber(
        Math.ceil(sourcePosition),
        lowerSourceIndex,
        safeSourceWidth - 1,
      );
      const blend =
        lowerSourceIndex === upperSourceIndex
          ? 0
          : sourcePosition - lowerSourceIndex;

      for (let rowIndex = 0; rowIndex < height; rowIndex += 1) {
        const sourceRowPosition = resolveSourceRowPosition(rowIndex);
        const [red, green, blue] = getSpectrogramPaletteColor(
          sampleMatrixValue(lowerSourceIndex, sourceRowPosition) * (1 - blend) +
            sampleMatrixValue(upperSourceIndex, sourceRowPosition) * blend,
        );
        const pixelIndex = (rowIndex * width + columnIndex) * 4;
        imageData.data[pixelIndex] = red;
        imageData.data[pixelIndex + 1] = green;
        imageData.data[pixelIndex + 2] = blue;
        imageData.data[pixelIndex + 3] = 255;
      }
    }

    context.putImageData(imageData, 0, 0);
    return;
  }

  for (let columnIndex = 0; columnIndex < width; columnIndex += 1) {
    const sourcePosition =
      safeViewportStartRatio * Math.max(safeSourceWidth - 1, 0) +
      (columnIndex / Math.max(width - 1, 1)) * sourceSpanWidth;
    const lowerSourceIndex = clampNumber(
      Math.floor(sourcePosition),
      0,
      safeSourceWidth - 1,
    );
    const upperSourceIndex = clampNumber(
      Math.ceil(sourcePosition),
      lowerSourceIndex,
      safeSourceWidth - 1,
    );
    const blend =
      lowerSourceIndex === upperSourceIndex
        ? 0
        : sourcePosition - lowerSourceIndex;

    for (let rowIndex = 0; rowIndex < height; rowIndex += 1) {
      const sourceRowPosition = resolveSourceRowPosition(rowIndex);
      const [red, green, blue] = getSpectrogramPaletteColor(
        sampleMatrixValue(lowerSourceIndex, sourceRowPosition) * (1 - blend) +
          sampleMatrixValue(upperSourceIndex, sourceRowPosition) * blend,
      );
      context.fillStyle = `rgb(${red}, ${green}, ${blue})`;
      context.fillRect(columnIndex, rowIndex, 1, 1);
    }
  }
}

export function paintWaveformPlaceholder(context, width, height) {
  if (!context || width <= 0 || height <= 0) {
    return;
  }

  const backgroundGradient = context.createLinearGradient(0, 0, 0, height);
  backgroundGradient.addColorStop(0, "#fbf7f2");
  backgroundGradient.addColorStop(1, "#f2ebe2");
  context.fillStyle = backgroundGradient;
  context.fillRect(0, 0, width, height);

  context.fillStyle = "rgba(81, 73, 64, 0.12)";
  [0, 0.5, 1].forEach((normalizedY) => {
    context.fillRect(
      0,
      Math.round(normalizedY * Math.max(height - 1, 1)),
      width,
      1,
    );
  });
}

export function paintWaveformChart(
  context,
  width,
  height,
  columns,
  viewportOptions = {},
) {
  if (!context || width <= 0 || height <= 0) {
    return;
  }

  paintWaveformPlaceholder(context, width, height);

  if (!columns?.length) {
    return;
  }

  const safeSourceWidth = Math.max(columns.length, 1);
  const safeViewportSpanRatio = clampNumber(
    viewportOptions.sourceSpanRatio ?? 1,
    1 / safeSourceWidth,
    1,
  );
  const maxViewportStartRatio = Math.max(1 - safeViewportSpanRatio, 0);
  const safeViewportStartRatio = clampNumber(
    viewportOptions.sourceStartRatio ?? 0,
    0,
    maxViewportStartRatio,
  );
  const sourceSpanWidth =
    safeViewportSpanRatio * Math.max(safeSourceWidth - 1, 0);

  for (let columnIndex = 0; columnIndex < width; columnIndex += 1) {
    const sourcePosition =
      safeViewportStartRatio * Math.max(safeSourceWidth - 1, 0) +
      (columnIndex / Math.max(width - 1, 1)) * sourceSpanWidth;
    const lowerSourceIndex = clampNumber(
      Math.floor(sourcePosition),
      0,
      safeSourceWidth - 1,
    );
    const upperSourceIndex = clampNumber(
      Math.ceil(sourcePosition),
      lowerSourceIndex,
      safeSourceWidth - 1,
    );
    const blend =
      lowerSourceIndex === upperSourceIndex
        ? 0
        : sourcePosition - lowerSourceIndex;
    const lowerColumn = columns[lowerSourceIndex] || {
      minAmplitudeMv: 0,
      maxAmplitudeMv: 0,
    };
    const upperColumn = columns[upperSourceIndex] || lowerColumn;
    const maxAmplitudeMv =
      lowerColumn.maxAmplitudeMv * (1 - blend) +
      upperColumn.maxAmplitudeMv * blend;
    const minAmplitudeMv =
      lowerColumn.minAmplitudeMv * (1 - blend) +
      upperColumn.minAmplitudeMv * blend;
    const topY = Math.round(
      normalizeWaveformAmplitude(maxAmplitudeMv) * Math.max(height - 1, 1),
    );
    const bottomY = Math.round(
      normalizeWaveformAmplitude(minAmplitudeMv) * Math.max(height - 1, 1),
    );

    context.fillStyle = "rgba(16, 122, 146, 0.28)";
    context.fillRect(columnIndex, topY, 1, Math.max(bottomY - topY, 1));
    context.fillStyle = "rgba(16, 122, 146, 0.82)";
    context.fillRect(columnIndex, topY, 1, 1);
    context.fillRect(columnIndex, bottomY, 1, 1);
  }

  context.fillStyle = "rgba(16, 122, 146, 0.4)";
  context.fillRect(
    0,
    Math.round(normalizeWaveformAmplitude(0) * Math.max(height - 1, 1)),
    width,
    1,
  );
}

export function getAudioContextConstructor() {
  if (typeof window === "undefined") {
    return null;
  }

  return window.AudioContext || window.webkitAudioContext || null;
}

let sharedSpectrogramAudioContext = null;

function getSharedSpectrogramAudioContext() {
  const AudioContextCtor = getAudioContextConstructor();
  if (!AudioContextCtor) {
    return null;
  }

  if (
    !sharedSpectrogramAudioContext ||
    sharedSpectrogramAudioContext.state === "closed"
  ) {
    sharedSpectrogramAudioContext = new AudioContextCtor();
  }

  return sharedSpectrogramAudioContext;
}

function decodeAudioData(audioContext, arrayBuffer) {
  return new Promise((resolve, reject) => {
    let settled = false;

    const resolveOnce = (value) => {
      if (settled) {
        return;
      }

      settled = true;
      resolve(value);
    };

    const rejectOnce = (error) => {
      if (settled) {
        return;
      }

      settled = true;
      reject(error);
    };

    try {
      const pendingDecode = audioContext.decodeAudioData(
        arrayBuffer.slice(0),
        resolveOnce,
        rejectOnce,
      );

      if (pendingDecode && typeof pendingDecode.then === "function") {
        pendingDecode.then(resolveOnce).catch(rejectOnce);
      }
    } catch (error) {
      rejectOnce(error);
    }
  });
}

async function buildSpectrogramAnalysis(audioBuffer) {
  const monoChannel = mixAudioBufferToMono(audioBuffer);
  if (!monoChannel.length) {
    throw new Error("Decoded audio buffer is empty");
  }

  const maxFrequencyHz = resolveSpectrogramMaxFrequency(audioBuffer.sampleRate);
  const waveformColumns = buildWaveformColumns(monoChannel);
  const bandRanges = buildSpectrogramBandRanges(
    SPECTROGRAM_HEIGHT,
    maxFrequencyHz,
    audioBuffer.sampleRate,
    SPECTROGRAM_FFT_SIZE,
  );
  const windowValues = buildSpectrogramWindowValues(SPECTROGRAM_FFT_SIZE);
  const matrix = Array.from(
    { length: SPECTROGRAM_WIDTH },
    () => new Float32Array(SPECTROGRAM_HEIGHT),
  );
  let minValue = Number.POSITIVE_INFINITY;
  let maxValue = Number.NEGATIVE_INFINITY;

  for (let columnIndex = 0; columnIndex < SPECTROGRAM_WIDTH; columnIndex += 1) {
    const centeredSampleIndex =
      SPECTROGRAM_WIDTH === 1
        ? Math.floor(monoChannel.length / 2)
        : Math.round(
            (columnIndex / (SPECTROGRAM_WIDTH - 1)) * (monoChannel.length - 1),
          );
    const magnitudes = performFftReal(
      buildSpectrogramFrame(
        monoChannel,
        centeredSampleIndex,
        SPECTROGRAM_FFT_SIZE,
        windowValues,
      ),
    );

    for (let rowIndex = 0; rowIndex < SPECTROGRAM_HEIGHT; rowIndex += 1) {
      const { startBinIndex, endBinIndex, emphasis, hasData } =
        bandRanges[rowIndex];

      if (!hasData) {
        matrix[columnIndex][rowIndex] = 0;
        continue;
      }

      let magnitudeSum = 0;

      for (
        let binIndex = startBinIndex;
        binIndex <= endBinIndex;
        binIndex += 1
      ) {
        magnitudeSum += magnitudes[binIndex] || 0;
      }

      const binCount = Math.max(endBinIndex - startBinIndex + 1, 1);
      const averageMagnitude = magnitudeSum / binCount;
      const spectralValue = Math.log10(1 + averageMagnitude * emphasis * 28);
      matrix[columnIndex][rowIndex] = spectralValue;
      minValue = Math.min(minValue, spectralValue);
      maxValue = Math.max(maxValue, spectralValue);
    }

    if ((columnIndex + 1) % 32 === 0) {
      await new Promise((resolve) => {
        window.setTimeout(resolve, 0);
      });
    }
  }

  return {
    columns: smoothSpectrogramMatrix(
      normalizeSpectrogramMatrix(matrix, minValue, maxValue),
    ),
    waveformColumns,
    duration: audioBuffer.duration,
    maxFrequencyHz,
  };
}

export async function loadSpectrogramAnalysis(mediaUrl) {
  if (spectrogramAnalysisCache.has(mediaUrl)) {
    return spectrogramAnalysisCache.get(mediaUrl);
  }

  if (pendingSpectrogramAnalysisLoads.has(mediaUrl)) {
    return pendingSpectrogramAnalysisLoads.get(mediaUrl);
  }

  const audioContext = getSharedSpectrogramAudioContext();
  if (!audioContext) {
    throw new Error("AudioContext unavailable");
  }

  if (typeof fetch !== "function") {
    throw new Error("Fetch unavailable");
  }

  const pendingLoad = fetch(mediaUrl)
    .then((response) => {
      if (!response.ok) {
        throw new Error(`Media fetch failed with status ${response.status}`);
      }

      return response.arrayBuffer();
    })
    .then((arrayBuffer) => decodeAudioData(audioContext, arrayBuffer))
    .then((audioBuffer) => buildSpectrogramAnalysis(audioBuffer))
    .then((analysis) => {
      spectrogramAnalysisCache.set(mediaUrl, analysis);
      return analysis;
    })
    .finally(() => {
      pendingSpectrogramAnalysisLoads.delete(mediaUrl);
    });

  pendingSpectrogramAnalysisLoads.set(mediaUrl, pendingLoad);
  return pendingLoad;
}
